<script setup lang="ts" generic="T">
import { ref, watch, computed, capitalize } from "vue";
import AutoComplete from "primevue/autocomplete";
import type { AutoCompleteCompleteEvent } from "primevue/autocomplete";
import Button from "primevue/button";
import Message from "primevue/message";
import { singular } from "pluralize";
import axios from "src/api/mutator/axios";
import { useDebounceFn } from "@vueuse/core";

interface Props {
  entity: string;
  entityLabel?: Array<keyof T | {
    relation: keyof T;
    labels: string[];
  }>;
  entityValue?: keyof T & string;
  labelSeparator?: string;
  placeholder?: string;
  label?: string;
  error?: string;
  requests?: any;
  addButtonOnly?: boolean;
  addButtonLabel?: string;
  returnEntity?: boolean;
  headless?: boolean;
  isChangeDetectionEnabled?: boolean;
  clearable?: boolean;
  hideSearchButton?: boolean;
  entityUrlParams?: Record<string, string | number>;
  relatedFields?: {
    field: string;
    entity: string;
    relationKey?: string;
    value: string | Record<string, any> | null;
  }[];
}

const modelValue = defineModel<string | Record<string, any> | null>();

const props = withDefaults(defineProps<Props>(), {
  placeholder: "Search...",
  label: "",
  entityValue: "id" as keyof T & string,
  labelSeparator: " - ",
  isChangeDetectionEnabled: false,
  clearable: true,
  hideSearchButton: false,
  entityUrlParams: () => ({}),
  relatedFields: () => [],
});

const emit = defineEmits<{
  blur: [event: FocusEvent];
  input: [event: { value: string }];
  change: [event: { value: string }];
  add: [];
}>();

const PAGE_SIZE = 25;
const autoCompleteRef = ref();
const selectedEntityData = ref<Record<string, any> | null>(null);
const pendingEntityData = ref<Record<string, any> | null>(null);
const suggestions = ref<Record<string, any>[]>([]);
const loading = ref(false);
const currentQuery = ref("");
const currentPage = ref(1);
const hasMore = ref(false);
const onSelectCallback = ref<((entity: Record<string, any>) => void) | null>(null);
const isManualChange = ref(false);

const searchFields = computed((): string[] => {
  if (!props.entityLabel) return ["name"];
  const labels = Array.isArray(props.entityLabel) ? props.entityLabel : [props.entityLabel];
  return labels.filter(l => typeof l === "string").map(l => String(l));
});

function getOptionLabel(entity: Record<string, any>): string {
  if (!entity || typeof entity === "string") return entity || "";
  if (!props.entityLabel) return entity.name || entity.id || "";

  const labels = Array.isArray(props.entityLabel) ? props.entityLabel : [props.entityLabel];
  return labels.map(lbl => {
    if (typeof lbl === "string") return entity[lbl as string];
    const rel = (lbl as any).relation as string;
    const sublabels = (lbl as any).labels as string[];
    const relObj = entity[rel];
    if (!relObj) return "";
    return sublabels.map((l: string) => relObj[l]).filter(Boolean).join(" ");
  }).filter(Boolean).join(props.labelSeparator);
}

async function fetchEntities(query: string, page: number, append = false) {
  loading.value = true;
  try {
    const params: Record<string, any> = { page, limit: PAGE_SIZE };
    const body: Record<string, any> = {};

    if (query) {
      searchFields.value.forEach((field) => {
        body[field] = query;
      });
      if (searchFields.value.length > 1) {
        body.match_mode = "or";
      }
    }

    let url = `/api/${props.entity}`;
    Object.entries(props.entityUrlParams).forEach(([key, value]) => {
      url = url.replace(`{${key}}`, String(value));
    });

    const response = await axios.post(url, body, { params });
    const data = response.data?.data ?? [];
    const totalPages = response.data?.total_pages ?? 1;

    suggestions.value = append ? [...suggestions.value, ...data] : data;
    hasMore.value = page < totalPages;
    currentPage.value = page;
  } catch {
    if (!append) { suggestions.value = []; hasMore.value = false; }
  } finally {
    loading.value = false;
  }
}

const debouncedComplete = useDebounceFn(async (event: AutoCompleteCompleteEvent) => {
  currentQuery.value = event.query;
  await fetchEntities(event.query, 1);
}, 300);

function handleListScroll(event: Event) {
  if (loading.value || !hasMore.value) return;
  const el = event.target as HTMLElement;
  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
    fetchEntities(currentQuery.value, currentPage.value + 1, true);
  }
}

const pt = computed(() => ({
  listContainer: { onScroll: handleListScroll },
}));

const showMismatchWarning = computed(() => {
  if (!props.isChangeDetectionEnabled || !selectedEntityData.value || !props.relatedFields.length) {
    return false;
  }

  return props.relatedFields.some(field => {
    const currentValue = field.value;
    const selectedValue = selectedEntityData.value?.[field.field];

    if (!currentValue || !selectedValue || typeof currentValue === "string" || typeof selectedValue === "string") {
      return false;
    }

    return currentValue.id !== selectedValue.id;
  });
});

const showChangeModal = computed(() => {
  return pendingEntityData.value !== null;
});

watch(
  () => props.relatedFields,
  (newFields) => {
    if (!props.isChangeDetectionEnabled) return;

    if (isManualChange.value) {
      isManualChange.value = false;
      return;
    }

    newFields.forEach(field => {
      const relationKey = field.relationKey || singular(props.entity);
      const relatedObject = field.value && typeof field.value === "object" ? field.value[relationKey] : null;

      if (!relatedObject) return;

      if (!selectedEntityData.value) {
        selectedEntityData.value = relatedObject;
        modelValue.value = props.returnEntity ? relatedObject : relatedObject[props.entityValue];
        handleChange(relatedObject[props.entityValue]);
        return;
      }

      if (selectedEntityData.value.id !== relatedObject.id) {
        pendingEntityData.value = relatedObject;
      }
    });
  },
  { deep: true },
);

const getEntityDisplayValue = (entity: Record<string, any> | null) => {
  if (!entity) return "";
  const nameFields = ["name", "title", "label", "description"];
  for (const field of nameFields) {
    if (entity[field]) return entity[field];
  }
  return entity.id;
};

function handleChange(value: string) {
  const blurEvent = new FocusEvent("blur");
  emit("change", { value });
  emit("blur", blurEvent);
  emit("input", { value });
}

function handleEntitySelection(entity: Record<string, any>) {
  if (!props.isChangeDetectionEnabled) {
    selectedEntityData.value = entity;
    const entityValue = String(entity[props.entityValue as string]);
    modelValue.value = props.returnEntity ? entity : entityValue;
    handleChange(entityValue);
    return;
  }

  isManualChange.value = true;
  selectedEntityData.value = entity;
  const entityValue = String(entity[props.entityValue as string]);
  modelValue.value = props.returnEntity ? entity : entityValue;
  handleChange(entityValue);
}

function onItemSelect(event: { value: Record<string, any> }) {
  handleEntitySelection(event.value);
}

function onClear() {
  selectedEntityData.value = null;
  pendingEntityData.value = null;
  modelValue.value = null;
  handleChange("");
}

function onBlur() {
  if (typeof selectedEntityData.value === "string") {
    selectedEntityData.value = null;
  }
}

function acceptChange() {
  if (pendingEntityData.value) {
    selectedEntityData.value = pendingEntityData.value;
    const entityValue = String(pendingEntityData.value[props.entityValue as string]);
    modelValue.value = props.returnEntity ? pendingEntityData.value : entityValue;
    handleChange(entityValue);
    pendingEntityData.value = null;
  }
}

function rejectChange() {
  pendingEntityData.value = null;
}

function openDrawer() {
  const input = autoCompleteRef.value?.$el?.querySelector("input");
  if (input) {
    input.focus();
  }
  return {
    onSelect: (callback: (entity: Record<string, any>) => void) => {
      onSelectCallback.value = callback;
    },
  };
}

defineExpose({
  openDrawer,
});

watch(
  () => selectedEntityData.value,
  (newValue) => {
    if (newValue && onSelectCallback.value) {
      onSelectCallback.value(newValue);
      onSelectCallback.value = null;
    }
  },
);

watch(
  () => modelValue.value,
  async (newValue) => {
    if (!newValue) {
      selectedEntityData.value = null;
      pendingEntityData.value = null;
      return;
    }

    if (typeof newValue === "string" && !selectedEntityData.value) {
      try {
        const { data: entityData } = await axios.get(`/api/${singular(props.entity)}/${newValue}`);
        if (entityData) {
          selectedEntityData.value = entityData;
        }
      } catch {
        // Entity not found by ID
      }
    }
  },
  { immediate: true },
);
</script>

<template>
  <div>
    <template v-if="!headless">
      <label v-if="label" class="block mb-2">{{ label }}</label>
      <div class="flex flex-col gap-2">
        <div class="flex gap-2 items-start">
          <AutoComplete
            v-if="!addButtonOnly"
            ref="autoCompleteRef"
            v-model="selectedEntityData"
            :suggestions="suggestions"
            :option-label="getOptionLabel"
            :loading="loading"
            :placeholder="placeholder"
            :show-clear="clearable"
            :pt="pt"
            :complete-on-focus="true"
            scroll-height="250px"
            fluid
            force-selection
            dropdown
            @complete="debouncedComplete"
            @item-select="onItemSelect"
            @clear="onClear"
            @blur="onBlur"
          >
            <template #option="{ option }">
              <slot name="item" :item="option">
                <span>{{ getOptionLabel(option) }}</span>
              </slot>
            </template>
          </AutoComplete>

          <Button
            v-if="addButtonOnly"
            :label="addButtonLabel || 'Add'"
            icon="pi pi-plus"
            @click="openDrawer()"
          />
          <slot name="actions" />
        </div>

        <div v-if="showMismatchWarning"
          class="flex items-center gap-2 text-sm text-warning mt-1 px-1">
          <i class="pi pi-exclamation-triangle" />
          <span>{{ capitalize(singular(props.entity)) }} belongs to a different {{ props.relatedFields[0]?.entity ?? 'organisation' }}</span>
        </div>

        <div v-if="showChangeModal"
          class="fixed inset-0 flex items-center justify-center z-50">
          <div class="absolute inset-0 bg-gray-900/80 backdrop-blur-sm" @click="rejectChange" />
          <div class="bg-gray-800 rounded-lg shadow-xl border border-warning/20 w-full max-w-lg m-4 z-10">
            <div class="p-4 border-b border-gray-700 flex items-center gap-3">
              <div class="w-10 h-10 rounded-full bg-warning/20 flex items-center justify-center flex-shrink-0">
                <i class="pi pi-exclamation-triangle text-warning text-lg" />
              </div>
              <div class="flex-grow">
                <h3 class="text-lg font-medium text-gray-200">Update Required</h3>
                <p class="text-sm text-gray-400">The selected item belongs to a different organisation</p>
              </div>
            </div>
            <div class="p-4 space-y-4">
              <div class="space-y-2">
                <div class="text-sm font-medium text-gray-300">Current {{ capitalize(singular(props.entity)) }}</div>
                <div class="p-3 bg-gray-900/50 rounded-lg flex items-center gap-3">
                  <i class="pi pi-building text-gray-400" />
                  <span>{{ getEntityDisplayValue(selectedEntityData) }}</span>
                </div>
                <div class="flex items-center gap-2 text-sm text-gray-400 px-3">
                  <i class="pi pi-arrow-down" />
                  <span>Will be changed to</span>
                </div>
                <div class="p-3 bg-warning/10 rounded-lg flex items-center gap-3 border border-warning/20">
                  <i class="pi pi-building text-warning" />
                  <span class="text-warning">{{ getEntityDisplayValue(pendingEntityData) }}</span>
                </div>
              </div>
              <template v-for="field in props.relatedFields" :key="field.field">
                <div v-if="pendingEntityData?.[field.field]" class="space-y-2">
                  <div class="text-sm font-medium text-gray-300 flex items-center gap-2">
                    <span>Related {{ capitalize(singular(field.entity)) }}</span>
                    <span class="text-xs text-gray-500">(will be updated)</span>
                  </div>
                  <div class="p-3 bg-gray-900/50 rounded-lg flex items-center gap-3">
                    <i class="pi pi-link text-gray-400" />
                    <span>{{ getEntityDisplayValue(selectedEntityData?.[field.field]) || 'None' }}</span>
                  </div>
                  <div class="flex items-center gap-2 text-sm text-gray-400 px-3">
                    <i class="pi pi-arrow-down" />
                    <span>Will be changed to</span>
                  </div>
                  <div class="p-3 bg-warning/10 rounded-lg flex items-center gap-3 border border-warning/20">
                    <i class="pi pi-link text-warning" />
                    <span class="text-warning">{{ getEntityDisplayValue(pendingEntityData[field.field]) }}</span>
                  </div>
                </div>
              </template>
            </div>
            <div class="p-4 border-t border-gray-700 flex items-center justify-end gap-3">
              <Button icon="pi pi-times" label="Cancel" severity="secondary" text @click="rejectChange" />
              <Button icon="pi pi-check" label="Update" severity="warning" @click="acceptChange" />
            </div>
          </div>
        </div>

        <Message v-if="error" severity="error" size="small" variant="simple">
          {{ error }}
        </Message>
      </div>
    </template>
    <AutoComplete
      v-else
      ref="autoCompleteRef"
      v-model="selectedEntityData"
      :suggestions="suggestions"
      :option-label="getOptionLabel"
      :loading="loading"
      :placeholder="placeholder"
      :pt="pt"
      :complete-on-focus="true"
      scroll-height="250px"
      fluid
      force-selection
      dropdown
      @complete="debouncedComplete"
      @item-select="onItemSelect"
      @clear="onClear"
      @blur="onBlur"
    />
  </div>
</template>
